import { createContext, useCallback, useContext, useRef } from 'react';
import { useLocation } from 'react-router-dom';

type NavigationHistory = {
  getHistoryStack: () => string[];
  getPrevious: () => string | undefined;
};

export const NavigationHistoryContext = createContext(null as unknown as NavigationHistory);

export const useNavigationHistory = () => {
  const { getHistoryStack, getPrevious } = useContext(NavigationHistoryContext);

  return { getHistoryStack, getPrevious };
};

const limit = 50; // Limit of paths on the history stack

const NavigationHistoryProvider: FC = ({ children }) => {
  const historyRef = useRef<string[]>([]);
  const { pathname } = useLocation();

  const pushToHistory = useCallback((path: string) => {
    if (historyRef.current.length === limit) {
      historyRef.current.shift();
    }

    historyRef.current.push(path);
  }, []);

  const getHistoryStack = useCallback(() => historyRef.current, []);
  const getLast = useCallback(() => historyRef.current[historyRef.current.length - 1], []);
  const getPrevious = useCallback(() => historyRef.current[historyRef.current.length - 2], []);

  if (getLast() !== pathname) {
    pushToHistory(pathname);
  }

  return (
    <NavigationHistoryContext.Provider value={{ getHistoryStack, getPrevious }}>
      {children}
    </NavigationHistoryContext.Provider>
  );
};

export default NavigationHistoryProvider;
